/**
 * Login page — one piece of the sign-in flow. The full mechanism:
 *
 *   1. BOOT (every page load, App.tsx "bootstrap" effect):
 *      - URL contains an OAuth response (#access_token=... from Cognito)?
 *        -> status = LOADING, wait for step 3. Login shows the spinner.
 *      - Otherwise ask Amplify for an existing session:
 *        -> valid session: dispatch authAction -> step 4 (no click needed)
 *        -> no session:    status = IDLE -> Login shows the button.
 *
 *   2. CLICK (handleLogin below):
 *      status = LOADING (spinner). If a valid local session already exists,
 *      skip the redirect: dispatch authAction -> step 4 directly. Otherwise
 *      Auth.federatedSignIn() does a full-page redirect to Cognito ->
 *      Microsoft 365 (app is unloaded). Microsoft redirects back with tokens
 *      in the URL -> back to step 1. (If the Microsoft SSO cookie is still
 *      warm, that round-trip is near-instant and looks like a page refresh.)
 *
 *   3. CALLBACK (index.tsx Hub listener):
 *      Amplify parses the tokens and fires Hub "signIn" -> dispatch
 *      authAction. The store exists synchronously (store.ts), so this event
 *      can never be dropped. Failure events -> status = FAILED.
 *
 *   4. PROFILE (redux/sagas/authSaga.ts):
 *      authAction -> LOADING -> GET usermgmt /login ->
 *      success: SUCCESS -> router (routes/router.tsx) swaps Login for the
 *      dashboard. failure: FAILED + authError -> alert shown below.
 *
 * Status lives in redux/slices/authSlice.ts (NOT persisted — it is derived
 * fresh from the Cognito session every boot). The router renders Login
 * whenever status !== SUCCESS; this component decides between button,
 * spinner, and error alert.
 *
 * ---------------------------------------------------------------------------
 * REDESIGNED 2026-06-11 (branch LoginFlowImprovments_TEC-7888).
 * The old mechanism, and why login used to take two clicks:
 *
 *   - The Redux store was only created AFTER downloading the three federated
 *     remote bundles (orderManagement/rateShop/invoiceManagement). The Hub
 *     "signIn" event from the OAuth callback usually fired before that and
 *     was silently dropped — Cognito had signed you in, Redux never heard.
 *   - There was no boot-time session check. Instead, handleLogin left an
 *     "isLoginInitiated" breadcrumb in localStorage and a Login-page effect
 *     tried to finish the sign-in after the redirect. That effect raced
 *     Amplify's token parsing; when it lost, it burned the breadcrumb and
 *     stranded the user on Login. App.tsx also wiped the breadcrumb on every
 *     mount. Second click worked because round-1 tokens were already cached.
 *   - authSaga swallowed profile-fetch errors -> stuck LOADING -> a 5-second
 *     watchdog silently dumped the user back at the button, no explanation.
 *   - authReducer was persisted to localStorage, so stale auth status
 *     ("logged in" / "loading") rehydrated on every boot.
 *   - The version checker could location.reload() on the sign-in click.
 *
 * What replaced it (the flow documented above):
 *   - store.ts: store is created synchronously; remotes download in the
 *     background and are injected via store.replaceReducer() — the Hub event
 *     can no longer be dropped, and Login renders without waiting on them.
 *   - App.tsx bootstrap: auth status is derived from the URL + Cognito
 *     session on every boot. Breadcrumb and 5s watchdog deleted; authReducer
 *     no longer persisted; version reload skipped while auth is unresolved.
 *   - Failures surface as FAILED + a visible error alert with the reason;
 *     a generous 20s safety net replaces the trigger-happy 5s one.
 */
import fpLogoOne from "../../styles/assets/logo_fpone_for_white_background.png";
import logoMicrosoft from "../../styles/assets/logo_microsoft.svg";
import React, { useEffect } from "react";
import { Auth } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import { authAction } from "../../redux/actions/authActions.ts";
import {
  setAuthIdle,
  setAuthLoading,
  setAuthFailed,
} from "../../redux/slices/authSlice.ts";
import {
  getIsAuthPending,
  getAuthError,
} from "../../redux/selectors/authSelectors.ts";
import { RootReducerState } from "../../utils/types.ts";

// MUI Components
import {
  Alert,
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";

// If auth stays unresolved this long, give the user the sign-in button back
// instead of an endless spinner. Generous on purpose: a normal sign-in
// (redirect + callback + profile fetch) resolves well within this.
const STUCK_AUTH_TIMEOUT_MS = 20000;

const Login = () => {
  const dispatch = useDispatch();
  // true while sign-in is unresolved (app booting, OAuth callback being
  // processed, or profile fetch in flight) — drives the spinner
  const isAuthPending = useSelector<RootReducerState, boolean>((state) =>
    getIsAuthPending(state),
  );
  // set when sign-in failed (e.g. the user-management call errored)
  const authError = useSelector<RootReducerState, string | null>((state) =>
    getAuthError(state),
  );

  // Runs when the "Sign in with Microsoft" button is clicked
  const handleLogin = async () => {
    // Show the spinner immediately; it stays up until auth resolves or the
    // browser leaves for Cognito.
    dispatch(setAuthLoading());

    // If a valid Cognito session already exists locally (e.g. we landed on
    // Login via an error state), skip the redirect entirely and go straight
    // to the profile fetch → home. No round-trip, no page "refresh".
    try {
      await Auth.currentSession();
      dispatch(authAction({}));
      return;
    } catch {
      // No usable local session — proceed with the real SSO redirect.
    }

    // Full-page redirect to Cognito Hosted UI → Microsoft 365 SSO.
    // The app is unloaded here; the flow continues on the way back:
    // callback → Hub "signIn" (index.tsx) → profile fetch (authSaga) → home.
    Auth.federatedSignIn({ customProvider: "FPGlobalSSO" }).catch((error) => {
      console.error("Failed to start federated sign-in: ", error);
      dispatch(setAuthFailed("Couldn't reach the sign-in page. Try again."));
    });
  };

  // Safety net: if auth never resolves (e.g. a dropped callback), return to
  // the sign-in button so the user is never stuck on a spinner.
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isAuthPending) {
      timer = setTimeout(() => {
        console.warn(
          `Auth unresolved after ${STUCK_AUTH_TIMEOUT_MS}ms → showing sign-in button`,
        );
        dispatch(setAuthIdle());
      }, STUCK_AUTH_TIMEOUT_MS);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isAuthPending, dispatch]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#ffffff",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 1,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={fpLogoOne}
            alt="FreightPlus One Logo"
            sx={{
              width: "100%",
              maxWidth: 320,
              mb: 4,
            }}
          />

          {/* Sign-in failed: show what went wrong, with the button below */}
          {authError && !isAuthPending && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {authError}
            </Alert>
          )}

          {/* Sign in button (spinner while sign-in is unresolved) */}
          <Box sx={{ width: "100%", mb: 3 }}>
            {isAuthPending ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <Button
                onClick={handleLogin}
                variant="contained"
                fullWidth
                size="large"
                startIcon={
                  <Box
                    component="img"
                    src={logoMicrosoft}
                    alt="Microsoft"
                    sx={{
                      width: 20,
                      height: 20,
                    }}
                  />
                }
                sx={{
                  backgroundColor: "#0078d4",
                  "&:hover": {
                    backgroundColor: "#006cbe",
                  },
                  py: 1.5,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                Sign in with Microsoft
              </Button>
            )}
          </Box>

          {/* Info text */}
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Use your company email to sign in through Microsoft 365. No need to
            create a new account.
          </Typography>

          <Typography
            variant="h6"
            align="center"
            color="primary"
            sx={{ mt: 3, fontWeight: 600 }}
          >
            Trouble signing in? Contact
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            {/* <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              Contact
            </Typography> */}
            <Typography variant="body2" color="text.secondary">
              itservices@freightplus.io
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
